// SCR-STAFF-400/401 (AP-BIL-03): اعتبار پکیج قلم (نام پکیج، ارزش، قیمت قفل‌شده برآورد و خاموش/روشن کردن مصرف — AC-BIL-071.1، 071.2؛ C-AGENT-816)
// و ورودی تخفیف مراجعه (کد کوپن و قاعده دستی انتخاب‌شده با علت کد نامعتبر — C-AGENT-822).
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { unwrap, type BilManualRuleView, type BilVisitLineView, type BilVisitView } from '@phoenix/api-client';
import { useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, FormDialog } from '@phoenix/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { parseCodes } from './PackagesPage';
import { useVisitCache } from './VisitDialogs';
import { Amount, bilKeys } from './common';

/** ستون «پکیج» قلم: اعتبار رزروشده و کلید مصرف. */
export function LinePackageCell({ visit, line, canManage }: { visit: BilVisitView; line: BilVisitLineView; canManage: boolean }) {
  const { t } = useTranslation('bil');
  const { tenantApi } = useTenantSession();
  const update = useVisitCache(visit.id);
  const toggle = useMutation({
    mutationFn: (usePackage: boolean) =>
      unwrap(tenantApi.PUT('/api/v1/billing/visits/{id}/lines/{lineId}/package', { params: { path: { id: visit.id, lineId: line.id } }, body: { usePackage } })),
    onSuccess: update,
  });
  const info = line.package;
  const editable = canManage && line.editable && line.status !== 'Cancelled' && !line.invoiceId;
  return (
    <Stack spacing={0.5} alignItems="flex-start" data-testid="bil-line-package" data-use-package={String(info?.usePackage ?? true)}>
      {line.packageCredit.amount > 0 ? <Amount money={line.packageCredit} testId="bil-line-package-credit" /> : <span>—</span>}
      {info?.packageName ? (
        <Typography variant="caption" color="text.secondary" data-testid="bil-line-package-name">
          {info.packageName}
        </Typography>
      ) : null}
      {info?.priceLocked ? <Chip size="small" variant="outlined" label={t('bil:lines.price_locked')} data-testid="bil-line-price-locked" /> : null}
      {editable && (info?.packageSaleId || info?.usePackage === false) ? (
        <Tooltip title={t('bil:lines.package_toggle_hint')}>
          <Button size="small" disabled={toggle.isPending} onClick={() => toggle.mutate(!(info?.usePackage ?? true))} data-testid="bil-line-package-toggle">
            {info?.usePackage === false ? t('bil:lines.use_package') : t('bil:lines.skip_package')}
          </Button>
        </Tooltip>
      ) : null}
      {toggle.error ? <ErrorState error={toggle.error} compact /> : null}
    </Stack>
  );
}

/** کد کوپن و قاعده دستی مراجعه. فهرست قواعد فقط با باز شدن دیالوگ خوانده می‌شود. */
export function DiscountInputsDialog({ visit, onClose }: { visit: BilVisitView; onClose: () => void }) {
  const { t } = useTranslation('bil');
  const { tenantApi, session } = useTenantSession();
  const update = useVisitCache(visit.id);
  const current = visit.discountInputs;
  const [codesText, setCodesText] = useState((current?.codes ?? []).join(' '));
  const [ruleIds, setRuleIds] = useState<string[]>(current?.ruleIds ?? []);
  const rules = useQuery({
    queryKey: bilKeys.manualRules(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/billing/discount-rules/manual')),
  });
  const save = useMutation({
    mutationFn: () => unwrap(tenantApi.PUT('/api/v1/billing/visits/{id}/discount-inputs', { params: { path: { id: visit.id } }, body: { codes: parseCodes(codesText), ruleIds } })),
    onSuccess: (view) => {
      update(view);
      if ((view.discountInputs?.invalidCodes.length ?? 0) === 0) {
        onClose();
      }
    },
  });
  const items = rules.data?.items ?? [];
  const selected = items.filter((rule) => ruleIds.includes(rule.id));
  const invalid = save.data?.discountInputs?.invalidCodes ?? current?.invalidCodes ?? [];
  return (
    <FormDialog
      open
      titleKey="bil:discount_inputs.title"
      onSubmit={() => save.mutate()}
      onClose={onClose}
      isDirty={codesText !== (current?.codes ?? []).join(' ') || ruleIds.length !== (current?.ruleIds ?? []).length}
      isSubmitting={save.isPending}
      submitError={save.error}
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        <TextField
          label={t('bil:packages.codes')}
          value={codesText}
          onChange={(event) => setCodesText(event.target.value)}
          helperText={t('bil:packages.codes_hint')}
          slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'bil-visit-codes' } }}
        />
        {rules.error ? <ErrorState error={rules.error} compact /> : null}
        <Autocomplete<BilManualRuleView, true>
          multiple
          options={items}
          value={selected}
          loading={rules.isPending}
          onChange={(_event, value) => setRuleIds(value.map((rule) => rule.id))}
          getOptionLabel={(rule) => `${rule.name} (${rule.code})`}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => <TextField {...params} label={t('bil:discount_inputs.manual_rules')} slotProps={{ htmlInput: { ...params.inputProps, 'data-testid': 'bil-visit-rules' } }} />}
        />
        {invalid.map((code) => (
          <Typography key={code.code} variant="body2" color="error" data-testid="bil-visit-invalid-code">
            {t('bil:packages.invalid_code', { code: code.code, reason: t(`cat:discount_reasons.${code.reason}`, { defaultValue: code.reason }) })}
          </Typography>
        ))}
      </Stack>
    </FormDialog>
  );
}
